import { useCallback, useMemo, useState } from 'react';
import type { Booking } from '@/hooks/queries/use-get-booking.query';

export const BOOKINGS_PAGE_SIZE = 10;

/**
 * Client-side “show N then load more” for API responses that return full arrays.
 * Extra “pages” are tracked per list fingerprint so a new list resets without effects.
 */
export function usePaginatedBookings(
  bookings: Booking[],
  pageSize: number = BOOKINGS_PAGE_SIZE
) {
  const fingerprint = useMemo(
    () => bookings.map(b => b.booking_id).join('|'),
    [bookings]
  );

  const [extraPagesByFingerprint, setExtraPagesByFingerprint] = useState<
    Record<string, number>
  >({});

  const extraPages = extraPagesByFingerprint[fingerprint] ?? 0;

  const visibleCount = Math.min(
    pageSize + extraPages * pageSize,
    bookings.length
  );

  const visibleBookings = useMemo(
    () => bookings.slice(0, visibleCount),
    [bookings, visibleCount]
  );

  const loadMore = useCallback(() => {
    setExtraPagesByFingerprint(prev => {
      const cur = prev[fingerprint] ?? 0;
      const curVisible = Math.min(pageSize + cur * pageSize, bookings.length);
      const nextVisible = Math.min(
        pageSize + (cur + 1) * pageSize,
        bookings.length
      );
      if (nextVisible === curVisible) {
        return prev;
      }
      return { ...prev, [fingerprint]: cur + 1 };
    });
  }, [bookings.length, fingerprint, pageSize]);

  const canLoadMore = visibleCount < bookings.length;

  return { visibleBookings, loadMore, canLoadMore };
}
